import React, { useState, useEffect } from 'react';
import { 
  Battery, 
  Zap, 
  MapPin, 
  Users, 
  DollarSign, 
  Activity,
  Settings,
  Bell,
  Menu,
  X
} from 'lucide-react';
import { hasPermission } from '../utils/permissions';

const Dashboard = ({ user, setActiveComponent }) => {
  console.log("DASHBOARD DEBUG - User:", user);
  console.log("DASHBOARD DEBUG - User permissions:", user?.permissions);
  console.log("DASHBOARD DEBUG - Analytics check:", hasPermission(user?.permissions, ["all", "analytics"]));
  console.log("DASHBOARD DEBUG - Billing check:", hasPermission(user?.permissions, ["all"]));
  console.log("DASHBOARD DEBUG - Users check:", hasPermission(user?.permissions, ["all", "users_view"]));
  console.log("DASHBOARD DEBUG - Stations check:", hasPermission(user?.permissions, ["all", "chargers_view"]));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalChargers: 0,
    activeChargers: 0,
    totalSessions: 0,
    totalRevenue: 0,
    recentSessions: [],
    chargers: []
  });

  useEffect(() => {
    loadDashboardData();
    loadLiveChargerData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/dashboard/stats-fixed', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        
        // Load recent sessions
        const sessionsResponse = await fetch("/api/charging-sessions/history", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          const sessions = sessionsData.sessions || [];
          setDashboardData(prev => ({
            ...prev,
            recentSessions: sessions.slice(0, 5)
          }));
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  // Load live charger data
  const loadLiveChargerData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/chargers/live-status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const liveChargers = data.chargers || [];
        
        // Update dashboard with live charger counts
        setDashboardData(prev => ({
          ...prev,
          totalChargers: liveChargers.length,
          activeChargers: liveChargers.filter(c => c.ocpp_connected).length,
          chargers: liveChargers.slice(0, 5)
        }));
      }
    } catch (error) {
      console.error('Error fetching live charger data:', error);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => {
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      yellow: 'bg-yellow-500'
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center">
          <div className={`p-3 rounded-lg ${colorClasses[color]} text-white`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome to LMPEnergy Dashboard</h1>
          <p className="text-gray-600 mt-2">Monitor and manage your EV charging network</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatCard
            title="Total Chargers"
            value={dashboardData.totalChargers}
            icon={Zap}
            color="blue"
          />
          <StatCard
            title="Active Chargers"
            value={dashboardData.activeChargers}
            icon={Activity}
            color="green"
          />
          <StatCard
            title="Total Sessions"
            value={dashboardData.totalSessions.toLocaleString()}
            icon={Battery}
            color="purple"
          />
          <StatCard
            title="Total Revenue"
            value={`£${dashboardData.totalRevenue.toFixed(2)}`}
            icon={DollarSign}
            color="yellow"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Sessions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Sessions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Session
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Charger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Energy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(dashboardData.recentSessions || []).map((session) => (
                    <tr key={session.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{session.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.charger_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          session.status === 'active' ? 'bg-green-100 text-green-800' :
                          session.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {session.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {parseFloat(session.energy_consumed || 0).toFixed(1)} kWh
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        £{parseFloat(session.total_cost || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Charger Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Charger Status</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {(dashboardData.chargers || []).map((charger) => (
                  <div key={charger.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        charger.status === 'online' ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{charger.name}</h4>
                        <p className="text-xs text-gray-500">{charger.location}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{charger.power_rating}kW</p>
                      <p className="text-xs text-gray-500">£{charger.price_per_kwh}/kWh</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

{/* ENHANCED QUICK ACTIONS - RESPONSIVE GRID */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hasPermission(user, ['all', 'users_view', 'analytics']) && (
              <button 
                onClick={() => setActiveComponent('users')} 
                className="flex items-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <Users className="w-8 h-8 text-blue-500 mr-4 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">Manage Users</p>
                  <p className="text-xs text-gray-500">View and edit user accounts</p>
                </div>
              </button>
            )}
            
            {hasPermission(user, ['all', 'chargers', 'chargers_view']) && (
              <button 
                onClick={() => setActiveComponent('stations')} 
                className="flex items-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-green-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <Zap className="w-8 h-8 text-green-500 mr-4 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">Monitor Stations</p>
                  <p className="text-xs text-gray-500">Check charger status and health</p>
                </div>
              </button>
            )}
            
            {hasPermission(user, ['all', 'analytics']) && (
              <button 
                onClick={() => setActiveComponent('analytics')} 
                className="flex items-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <Activity className="w-8 h-8 text-purple-500 mr-4 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">View Analytics</p>
                  <p className="text-xs text-gray-500">Performance insights and metrics</p>
                </div>
              </button>
            )}
            
            {hasPermission(user, ['all']) && (
              <button 
                onClick={() => setActiveComponent('billing')} 
                className="flex items-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-yellow-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <DollarSign className="w-8 h-8 text-yellow-500 mr-4 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">View Revenue</p>
                  <p className="text-xs text-gray-500">Check financial reports</p>
                </div>
              </button>
            )}
          </div>
        </div>


      </main>
    </div>
  );
};

export default Dashboard;
